'use client';

import { Button } from '@pickleballinc/ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPortal, DialogTitle } from '@pickleballinc/ui/components/ui/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { TieBreakerScoreFormat } from '@/entities/Matchup';
import { SequenceStatus, TieBreakerSequence } from '@/entities/rotations/GameSequence';
import { useGameSequencesMutation } from '@/hooks/tie-breaker/useRotationsMutation';
import { CreateGameSequencesPayload } from '@/services/actions/tie-breaker/rotations';

interface FinalTieBreakerScoreModalProps {
	isOpen: boolean;
	onClose: () => void;
	gameSequences: TieBreakerSequence[];
	matchupId: string;
	awayTeamTitle: string;
	homeTeamTitle: string;
	tieBreakerScoreFormat: TieBreakerScoreFormat | null;
	awayTeamScore?: string;
	homeTeamScore?: string;
}

export const FinalTieBreakerScoreModal = ({
	isOpen,
	onClose,
	gameSequences,
	matchupId,
	awayTeamTitle,
	homeTeamTitle,
	tieBreakerScoreFormat,
	awayTeamScore,
	homeTeamScore
}: FinalTieBreakerScoreModalProps) => {
	const [awayScore, setAwayScore] = useState<string>(awayTeamScore ?? '');
	const [homeScore, setHomeScore] = useState<string>(homeTeamScore ?? '');

	const invalidateQueries = useQueryClient();
	const { createSequence, isCreatingSequence } = useGameSequencesMutation(
		gameSequences[0]?.matchUuid || '',
		gameSequences[0]?.gameNumber.toString() || '1',
		matchupId
	);

	const handleComplete = () => {
		const payload: CreateGameSequencesPayload = {
			sequences: [
				{
					sequenceNumber: 1,
					sequenceStatus: SequenceStatus.PLAYED,
					matchUuid: gameSequences[0]?.matchUuid || '',
					gameNumber: 1,
					teamOnePlayerOneUuid: gameSequences[0]?.teamOnePlayerOneUuid || '',
					teamTwoPlayerOneUuid: gameSequences[0]?.teamTwoPlayerOneUuid || '',
					teamOneScore: Number(homeScore),
					teamTwoScore: Number(awayScore),
					isDirectFinalScore: true
				}
			]
		};

		createSequence.mutate(payload, {
			onSuccess: () => {
				onClose();
				invalidateQueries.invalidateQueries({ queryKey: ['MATCHUP_WINNER', matchupId] });
			}
		});
	};

	const handleClose = () => {
		setAwayScore(awayTeamScore ?? '');
		setHomeScore(homeTeamScore ?? '');
		onClose();
	};

	const game1Max = tieBreakerScoreFormat?.game1Max;
	const game1WinBy = tieBreakerScoreFormat?.game1WinBy ?? 1;
	const winnerScore = Math.max(Number(awayScore), Number(homeScore));
	const loserScore = Math.min(Number(awayScore), Number(homeScore));
	const isScoreFormatValid =
		game1Max === undefined ||
		(winnerScore === game1Max && loserScore <= game1Max - game1WinBy) ||
		(winnerScore > game1Max && loserScore === winnerScore - game1WinBy);
	const isScoreChanged = awayScore !== awayTeamScore || homeScore !== homeTeamScore;
	const isValid = awayScore !== '' && homeScore !== '' && isScoreFormatValid && isScoreChanged;

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogPortal>
				<DialogContent className="w-80 sm:w-96 rounded-md" hideClose>
					<DialogHeader>
						<DialogTitle>Enter final score</DialogTitle>
					</DialogHeader>

					<div className="flex flex-col divide-y divide-gray-200 border border-gray-200 rounded-md">
						<div className="flex items-center justify-between px-4 py-3">
							<span className="font-semibold text-sm">{awayTeamTitle} (A)</span>
							<input
								type="number"
								min={0}
								className="custom-number-input w-10 h-10 border border-primary-300 rounded-md text-center text-lg shrink-0 focus:outline-2 focus:outline-primary-500 focus:border-0"
								value={awayScore}
								onChange={(e) => setAwayScore(e.target.value)}
							/>
						</div>
						<div className="flex items-center justify-between px-4 py-3">
							<span className="font-semibold text-sm">{homeTeamTitle} (H)</span>
							<input
								type="number"
								min={0}
								className="custom-number-input w-10 h-10 border border-primary-300 rounded-md text-center text-lg shrink-0 focus:outline-2 focus:outline-primary-500 focus:border-0"
								value={homeScore}
								onChange={(e) => setHomeScore(e.target.value)}
							/>
						</div>
					</div>

					<DialogFooter className="gap-2">
						<Button variant="outline" onClick={handleClose} disabled={isCreatingSequence} className="flex-1">
							Cancel
						</Button>
						<Button onClick={handleComplete} disabled={!isValid || isCreatingSequence} className="flex-1">
							Complete
						</Button>
					</DialogFooter>
				</DialogContent>
			</DialogPortal>
		</Dialog>
	);
};
